import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import "./Navbar.css";
import { useNavigate } from "react-router-dom";
import { AppBar, Box, Toolbar, Typography, Menu, MenuItem } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import Logo from "../../assets/images/phx_logo_horiz.svg";
import TertiaryButton from "../Buttons/TertiaryButton/TertiaryButton";
import Logout from "../Buttons/Logout/Logout";
import GlobalSearch from "../GlobalSearch/GlobalSearch";
import TextPill from "../Pills/TextPill/TextPill";
import AlertNotifier from "../Alerts/Alert";
import NotificationsPanel from "../NotificationsPanel/NotificationsPanel";
import SecurityCheck from "../SecurityCheck/SecurityCheck";
import { AlertContext, UserPermissionsContext } from "../Dashboard/Dashboard";

export default function NavBar({
  pendingLoadsCount,
  handleTableAction
}) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const userPermissions = useContext(UserPermissionsContext);
  const { alert } = useContext(AlertContext);

  const navLinks = useMemo(() => [
    {
      name: "MATCHES",
      linkTo: "freight-matching",
      component: "CarrierSearch",
      action: "Read"
    },
    {
      name: "OFFERS",
      linkTo: "offer-boards",
      component: "CarrierOffersDashboard",
      action: "Read",
      notifications: pendingLoadsCount
    },
    {
      name: "LOAD BOARDS",
      linkTo: "load-boards",
      component: "CarrierSearch",
      action: "Read"
    },
    {
      name: "CARRIER SEARCH",
      linkTo: "carrier-search",
      component: "CarrierSearch",
      action: "Read"
    }
  ], [pendingLoadsCount]);

  const handleMenu = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleHomeClick = useCallback(() => {
    if (Object.keys(userPermissions).length) {
      navigate("/dashboard/load-boards");
    }
  }, [userPermissions]);

  const handleMyAccount = useCallback(() => {
    navigate("admin/my-account");
    handleClose();
  }, []);

  const handleUserManagement = useCallback(() => {
    navigate("admin/user-management");
    handleClose();
  }, []);

  const handleRegionManagement = useCallback(() => {
    navigate("admin/region-management");
    handleClose();
  }, []);

  const handleNavLink = useCallback((link) => {
    if (link.linkTo !== "load-boards") {
      handleTableAction({ action: link.linkTo });
    } else {
      navigate(`/dashboard/${link.linkTo}`);
    }
  }, []);

  useEffect(() => {
    if (pendingLoadsCount) {
      navLinks[1].notifications = pendingLoadsCount;
    }
  }, [pendingLoadsCount]);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" sx={{ backgroundColor: "white_opacity.100", boxShadow: "elevation2" }}>
        <Toolbar>
          <Box
            onClick={handleHomeClick}
            sx={{ "&:hover": { cursor: "pointer" } }}
          >
            <img
              src={Logo}
              alt="Phoenix Logo"
              title="Phoenix Logo"
              width="220"
            />
          </Box>
          <div className="Navlink-container">
            {navLinks.map((link, i) => (
              <SecurityCheck
                key={`nav-link-${i}`}
                componentName={link.component}
                action={link.action}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", cursor: "pointer", marginRight: "50px" }}
                  onClick={() => handleNavLink(link)}>
                  <Typography variant="h4" color="primary.60">
                    {link.name}
                  </Typography>
                  {link.notifications &&
                    <TextPill
                      value={link.notifications}
                      backgroundColor="error.70"
                      height="16px"
                      width="19px"
                      padding="2px 6px"
                      margin="0 0 0 5px"
                    />
                  }
                </Box>
              </SecurityCheck>
            ))}
          </div>
          <SecurityCheck
            componentName="CarrierSearch"
            action="Read"
          >
            <Box sx={{ marginRight: "12px" }}>
              <GlobalSearch handleTableAction={handleTableAction} />
            </Box>
          </SecurityCheck>
          <Box sx={{ marginRight: "-4px" }} >
            <NotificationsPanel />
          </Box>
          <TertiaryButton
            onClick={handleMenu}
            startIcon={PersonIcon}
          />
          <Menu
            id="navbar-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "navbar-button",
            }}
            slotProps={{
              paper: {
                sx: { boxShadow: "elevation3" },
              }
            }}
          >
            <SecurityCheck
              componentName="UserProfile"
              action="Read"
            >
              <MenuItem onClick={handleMyAccount}>
                <Typography variant="h4" color="black_solid.80" >
                  My Account
                </Typography>
              </MenuItem>
            </SecurityCheck>
            <SecurityCheck
              componentName="Admin"
              action="Read"
            >
              <MenuItem onClick={handleUserManagement}>
                <Typography variant="h4" color="black_solid.80">
                  User Management
                </Typography>
              </MenuItem>
            </SecurityCheck>
            <SecurityCheck
              componentName="Admin"
              action="Read"
            >
              <MenuItem onClick={handleRegionManagement}>
                <Typography variant="h4" color="black_solid.80">
                  Region Management
                </Typography>
              </MenuItem>
            </SecurityCheck>
            <MenuItem onClick={handleClose}>
              <Logout />
            </MenuItem>
          </Menu>
        </Toolbar>
        {alert && <AlertNotifier />}
      </AppBar>
    </Box>
  );
}

NavBar.propTypes = {
  pendingLoadsCount: PropTypes.number,
  handleTableAction: PropTypes.func
};